const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

class BackofficeService {
  constructor() {
    this.baseUrl = API_URL;
  }

  async getCsrfToken() {
    const response = await fetch(`${this.baseUrl}/auth/csrf/`, {
      credentials: 'include',
    });
    const data = await response.json();
    return data.csrfToken;
  }

  async request(endpoint, options = {}, isFormData = false) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = isFormData ? {} : { 'Content-Type': 'application/json' };
    
    const config = {
      credentials: 'include',
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    };

    // Adiciona CSRF token para métodos que modificam dados
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method)) {
      const csrfToken = await this.getCsrfToken();
      config.headers['X-CSRFToken'] = csrfToken;
    }

    const response = await fetch(url, config);
    
    if (response.status === 204) {
      return null;
    }

    const data = await response.json();
    
    if (!response.ok) {
      const detail = data.detail || data.error;
      if (detail) throw new Error(detail);
      // Field-level validation errors: flatten into readable message
      const fieldErrors = Object.entries(data)
        .map(([field, errs]) => `${field}: ${Array.isArray(errs) ? errs.join(', ') : errs}`)
        .join(' | ');
      throw new Error(fieldErrors || `Erro ${response.status}`);
    }
    
    return data;
  }

  // ESPN fixtures for the bet form's game picker
  async searchEspnGames({ date, sport, q }) {
    const params = new URLSearchParams({ date, sport, q: q || '' });
    const data = await this.request(`/live-scores/search/?${params}`);
    return data.events || [];
  }

  // BETS
  async getBets() {
    const data = await this.request('/bets/?limit=1000');
    return data.results || data;
  }

  async getBet(id) {
    return this.request(`/bets/${id}/`);
  }

  async createBet(data) {
    return this.request('/bets/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBet(id, data) {
    return this.request(`/bets/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteBet(id) {
    return this.request(`/bets/${id}/`, {
      method: 'DELETE',
    });
  }

  // CHALLENGES
  async getChallenges() {
    const data = await this.request('/challenges/?limit=1000');
    return data.results || data;
  }

  async getChallenge(id) {
    return this.request(`/challenges/${id}/`);
  }

  async createChallenge(data) {
    return this.request('/challenges/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateChallenge(id, data) {
    return this.request(`/challenges/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteChallenge(id) {
    return this.request(`/challenges/${id}/`, {
      method: 'DELETE',
    });
  }

  // PARTNERS
  async getPartners() {
    const data = await this.request('/partners/?limit=1000');
    return data.results || data;
  }

  async getPartner(id) {
    return this.request(`/partners/${id}/`);
  }

  async createPartner(data, isFormData = false) {
    const options = {
      method: 'POST',
    };
    
    if (isFormData) {
      options.body = data;
      options.headers = {}; // Let browser set Content-Type with boundary
    } else {
      options.body = JSON.stringify(data);
    }
    
    return this.request('/partners/', options, isFormData);
  }

  async updatePartner(id, data, isFormData = false) {
    const options = {
      method: 'PATCH', // Use PATCH for partial updates with FormData
    };
    
    if (isFormData) {
      options.body = data;
      options.headers = {}; // Let browser set Content-Type with boundary
    } else {
      options.body = JSON.stringify(data);
    }
    
    return this.request(`/partners/${id}/`, options, isFormData);
  }

  async deletePartner(id) {
    return this.request(`/partners/${id}/`, {
      method: 'DELETE',
    });
  }

  // SCOREBOARD
  async getScoreboard() {
    return this.request('/scoreboard/');
  }

  async updateScoreboard(data) {
    return this.request('/scoreboard/', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }
}

const backofficeService = new BackofficeService();
export default backofficeService;
